/**
 * ExperiencePreferenceConfig - visual toggle UI for the host's per-experience
 * preference selections.
 *
 * Each Master Experience the host offers is rendered as a card with:
 *   - A list of category-specific Preference Fields the host can toggle.
 *   - Three universal toggles: Allergies, Dietary, Accessibility.
 *
 * Saves to PATCH /api/host/{provider_id}/experiences/{experience_id}/preference-config
 *
 * Default state (when the host has never saved): all category fields enabled,
 * Allergies ON, Dietary OFF, Accessibility OFF - matching the user's spec.
 */
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Props:
 *   providerId       – the host's provider_id
 *   experienceId     – the Master Experience ID this card configures
 *   experienceLabel  – human-readable name (e.g., "Bridal Glam")
 *   category         – category slug for fetching the field list
 */
export default function ExperiencePreferenceConfig({
  providerId,
  experienceId,
  experienceLabel,
  category,
}) {
  const [fields, setFields] = useState([]);
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  // Load fields for this category + the host's saved config (if any)
  useEffect(() => {
    if (!category) return;
    fetch(`${API}/preferences/category/${category.toLowerCase()}/fields`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        // Filter out universal keys; they have their own dedicated toggles below.
        const UNIVERSAL = new Set(["allergies", "dietary", "accessibility"]);
        setFields((d?.fields || []).filter(f => !UNIVERSAL.has(f.key)));
      });
    fetch(`${API}/host/${providerId}/experiences/${experienceId}/preference-config`)
      .then(r => r.ok ? r.json() : null)
      .then(setConfig);
  }, [category, providerId, experienceId]);

  if (!config) return null;

  const selectedSet = new Set(config.selected_field_ids || []);
  const isFieldOn = (fid) => !config.selected_field_ids || selectedSet.has(fid);

  const save = async (patch) => {
    setSaving(true);
    const next = { ...config, ...patch, is_default: false };
    setConfig(next);
    try {
      const token = localStorage.getItem("session_token");
      const r = await fetch(
        `${API}/host/${providerId}/experiences/${experienceId}/preference-config`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            selected_field_ids: next.selected_field_ids,
            require_allergies: next.require_allergies,
            require_dietary: next.require_dietary,
            require_accessibility: next.require_accessibility,
          }),
        },
      );
      if (!r.ok) throw new Error(await r.text());
    } catch (e) {
      console.error(e);
      toast.error("Couldn't save preference config");
    } finally {
      setSaving(false);
    }
  };

  const toggleField = (fid) => {
    const current = new Set(
      config.selected_field_ids === null
        ? fields.map(f => f.field_id)
        : config.selected_field_ids,
    );
    if (current.has(fid)) {
      current.delete(fid);
    } else {
      current.add(fid);
    }
    save({ selected_field_ids: Array.from(current) });
  };

  return (
    <Card className="rounded-xl border-stone-200" data-testid={`exp-pref-config-${experienceId}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center justify-between">
          <span>{experienceLabel || experienceId}</span>
          {saving && <span className="text-[10px] text-stone-400">saving…</span>}
        </CardTitle>
        <p className="text-[11px] text-stone-500">
          Pick exactly which preferences clients answer when they're invited to this experience.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Category-specific fields */}
        {fields.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1.5">
              Category preferences
            </p>
            <div className="grid grid-cols-2 gap-y-1 gap-x-3">
              {fields.map(f => (
                <label
                  key={f.field_id}
                  className="flex items-center gap-2 text-xs text-stone-700 cursor-pointer select-none"
                  data-testid={`exp-field-toggle-${f.field_id}`}
                >
                  <Checkbox
                    checked={isFieldOn(f.field_id)}
                    onCheckedChange={() => toggleField(f.field_id)}
                  />
                  <span className="truncate">{f.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Universal toggles - Allergies / Dietary / Accessibility */}
        <div className="border-t border-stone-200 pt-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1.5">
            Universal toggles (rendered at the bottom of the form)
          </p>
          {[
            { key: "require_allergies",     label: "Collect Allergies",      hint: "Recommended on for safety." },
            { key: "require_dietary",       label: "Collect Dietary",        hint: "Off by default. Toggle on for culinary or events." },
            { key: "require_accessibility", label: "Collect Accessibility",  hint: "Off by default. Toggle on if relevant." },
          ].map(t => (
            <div key={t.key} className="flex items-center justify-between py-1.5">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-stone-800">{t.label}</p>
                <p className="text-[10px] text-stone-500">{t.hint}</p>
              </div>
              <Switch
                checked={!!config[t.key]}
                onCheckedChange={(v) => save({ [t.key]: v })}
                data-testid={`exp-${t.key.replace("require_", "")}-switch`}
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
